import { useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { complianceAllowedForTier } from '../../lib/compliance-tier.js';
import { readinessKpi } from '../../lib/fleet-kpis.js';
import { cardFrameworks } from './ComplianceCard.jsx';
import { COMPLIANCE_FEATURE } from '../../components/QlikUpsellCard.jsx';
import KpiCard from '../../components/KpiCard.jsx';

// The "Readiness N%" KPI on the customer's own Overview (ENTRAID-333): the lowest "% of criteria met" among the
// frameworks of the customer's most recent succeeded compliance report, read from the same customer-scoped reports
// API the Compliance pages use. Only roles that can read report content see it (admin, auditor), and never a tier
// without Compliance. Display only: the API enforces role and tier on its own. Never a zero for a missing value.

const when = (r) => String(r?.completedAt ?? r?.requestedAt ?? '');

/** The newest succeeded report of a reports list, or null. */
export function latestSucceeded(reports) {
  return (Array.isArray(reports) ? reports : [])
    .filter((r) => r && r.status === 'succeeded')
    .sort((a, b) => (when(a) < when(b) ? 1 : when(a) > when(b) ? -1 : 0))[0] ?? null;
}

/** The readinessKpi input for a reports list. */
export function readinessInput(reports) {
  const row = latestSucceeded(reports);
  if (!row) return { state: 'none' };
  const frameworks = cardFrameworks(row).map((f) => ({ name: f.name, percent: f.view.percent }));
  return { state: 'report', frameworks };
}

export default function ReadinessKpi({ customerId, role, tier, tierKnown = true, marketplaceListingUrl = '' }) {
  const content = role === 'admin' || role === 'auditor';
  const excluded = content && tierKnown && !complianceAllowedForTier(tier);
  const visible = content && !excluded;
  const [input, setInput] = useState({ state: 'loading' });

  useEffect(() => {
    if (!visible) return undefined;
    let live = true;
    api.complianceReports(customerId).then(
      (out) => { if (live) setInput(readinessInput(out?.reports)); },
      () => { if (live) setInput({ state: 'error' }); },
    );
    return () => { live = false; };
  }, [customerId, visible]);

  if (excluded) {
    // Not in the plan: the same card, the label and the two existing actions of the Compliance upsell (no status text, no separate block).
    const link = 'text-ipc-light hover:text-ipc-primary underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ipc-light';
    return (
      <KpiCard label="Readiness" kpi="readiness" title={COMPLIANCE_FEATURE.title}>
        <div data-readiness-plan="excluded" className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm font-medium">
          <a className={link} href={COMPLIANCE_FEATURE.mailto}>Request access</a>
          {marketplaceListingUrl && (
            <a className={link} href={marketplaceListingUrl} target="_blank" rel="noopener noreferrer">Change plan</a>
          )}
        </div>
      </KpiCard>
    );
  }
  if (!visible) return null;
  const k = readinessKpi(input);
  const caption = k.value == null
    ? (input.state === 'none' ? 'No report yet' : input.state === 'loading' ? 'Loading' : 'Not available right now')
    : 'Lowest across frameworks';
  return (
    <KpiCard
      label="Readiness"
      kpi="readiness"
      to={`/customers/${encodeURIComponent(customerId)}/reports`}
      title={k.title}
      ariaLabel={`${k.title}. Open Reports`}
      value={k.value}
      unit="%"
      variant="bar"
      percent={k.value}
      caption={caption}
    />
  );
}
